class OettingerShippingGuard {
  constructor() {
    this.countrySelectors = [
      '[data-oettinger-shipping-country]',
      '[data-shipping-estimator-country]',
      '.shipping-estimator select[name*="country" i]',
      '.shipping-estimator select[id*="country" i]',
      '.cart-shipping-estimator select[name*="country" i]',
      '.cart-shipping-estimator select[id*="country" i]',
      '[class*="shipping" i][class*="estimator" i] select[name*="country" i]',
      '[class*="shipping" i][class*="estimator" i] select[id*="country" i]',
      'cart-drawer select[name*="country" i]',
      'cart-drawer select[id*="country" i]',
      'cart-items select[name*="country" i]',
      'cart-items select[id*="country" i]',
      '#main-cart-footer select[name*="country" i]',
      '#main-cart-footer select[id*="country" i]',
    ];

    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleCartUpdate = this.handleCartUpdate.bind(this);
  }

  init() {
    document.addEventListener('change', this.handleChange);
    document.addEventListener('submit', this.handleSubmit, true);
    document.addEventListener('click', this.handleSubmit, true);
    document.addEventListener('cart-update', this.handleCartUpdate);
    this.validate();
  }

  handleChange(event) {
    if (this.isCountrySelect(event.target) || this.isTermsCheckbox(event.target)) this.validate();
  }

  handleSubmit(event) {
    const checkoutTrigger = this.getCheckoutTrigger(event);
    if (!checkoutTrigger) return;

    const state = this.validate();
    if (!state.hasConflict) return;

    event.preventDefault();
    event.stopPropagation();
    state.firstConflictItem?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  getCheckoutTrigger(event) {
    if (event.type === 'click') {
      return event.target.closest?.('[name="checkout"], [data-oettinger-cart-page-checkout], #CartDrawer-Checkout');
    }

    if (event.type !== 'submit' || !event.target.matches('#cart, #CartDrawer-Form')) return null;

    const submitter = event.submitter;
    if (this.isCheckoutTrigger(submitter)) return submitter;

    const activeElement = document.activeElement;
    if (this.isCheckoutTrigger(activeElement) && activeElement.form === event.target) return activeElement;

    return null;
  }

  isCheckoutTrigger(element) {
    return Boolean(
      element?.matches?.('[name="checkout"], [data-oettinger-cart-page-checkout], #CartDrawer-Checkout')
    );
  }

  handleCartUpdate() {
    window.setTimeout(() => this.validate(), 0);
  }

  validate() {
    const selectedCountry = this.getSelectedCountryCode();
    const hasCountryEstimate = selectedCountry !== null;
    const isGermany = selectedCountry === 'DE';
    const conflictItems = this.getRestrictedItems();
    const hasConflict = hasCountryEstimate && !isGermany && conflictItems.length > 0;

    this.updateLineItems(conflictItems, hasConflict);
    this.updateCheckoutButtons(hasConflict);
    this.updateContainers(hasConflict);

    return {
      hasConflict,
      firstConflictItem: conflictItems[0],
    };
  }

  getSelectedCountryCode() {
    const select = this.getCountrySelect();
    if (!select) return null;

    const selectedOption = select.selectedOptions?.[0];
    const rawCountry = selectedOption?.dataset.value || select.value || selectedOption?.value || selectedOption?.text || '';
    const normalizedCountry = rawCountry.trim().toLowerCase();

    if (!normalizedCountry) return null;
    if (['de', 'deu', 'germany', 'deutschland'].includes(normalizedCountry)) return 'DE';
    return rawCountry.trim().toUpperCase();
  }

  getCountrySelect() {
    const candidates = this.countrySelectors.flatMap((selector) => Array.from(document.querySelectorAll(selector)));
    return candidates.find((select) => this.isCountrySelect(select)) || null;
  }

  isCountrySelect(element) {
    if (!(element instanceof HTMLSelectElement)) return false;
    if (element.closest('localization-form, .localization-form, .country-selector, header, footer')) return false;

    const identifier = `${element.name || ''} ${element.id || ''} ${element.className || ''}`.toLowerCase();
    const hasCountryIdentifier = identifier.includes('country') || element.hasAttribute('data-oettinger-shipping-country');
    const isEstimatorScoped = Boolean(
      element.closest(
        '[data-oettinger-shipping-estimator], [data-shipping-estimator], .shipping-estimator, .cart-shipping-estimator, [class*="shipping" i][class*="estimator" i], cart-drawer, cart-items, #main-cart-footer'
      )
    );

    return hasCountryIdentifier && isEstimatorScoped;
  }

  getRestrictedItems() {
    const restrictedItems = Array.from(
      document.querySelectorAll('[data-oettinger-shipping-restriction="de_only"]')
    );
    return restrictedItems.filter((item, index, items) => items.indexOf(item) === index);
  }

  updateLineItems(conflictItems, hasConflict) {
    document.querySelectorAll('.cart-item.oettinger-shipping-guard__line-item--conflict').forEach((item) => {
      item.classList.remove('oettinger-shipping-guard__line-item--conflict');
      item.removeAttribute('aria-describedby');
    });

    if (!hasConflict) return;

    conflictItems.forEach((item) => {
      item.classList.add('oettinger-shipping-guard__line-item--conflict');
      item.setAttribute('aria-describedby', this.getMessageId(item));
    });
  }

  updateCheckoutButtons(hasConflict) {
    const checkoutButtons = document.querySelectorAll(
      '[name="checkout"], [data-oettinger-cart-page-checkout], #CartDrawer-Checkout'
    );

    checkoutButtons.forEach((button) => {
      const termsAccepted = this.areTermsAccepted(button);
      const shouldDisable = hasConflict || !termsAccepted;
      button.disabled = shouldDisable;
      if (shouldDisable) {
        button.setAttribute('disabled', 'true');
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('disabled');
        button.removeAttribute('aria-disabled');
      }
      button.classList.toggle('oettinger-shipping-guard__checkout--disabled', hasConflict);
    });
  }

  updateContainers(hasConflict) {
    document.querySelectorAll('cart-drawer, cart-items, #main-cart-footer').forEach((container) => {
      container.classList.toggle('oettinger-shipping-guard--blocked', hasConflict);
    });

    this.renderMessage(hasConflict);
  }

  renderMessage(hasConflict) {
    const targets = [
      document.querySelector('#main-cart-footer .cart__blocks'),
      document.querySelector('cart-drawer .drawer__footer'),
    ].filter(Boolean);

    targets.forEach((target) => {
      const messageId = this.getMessageId(target);
      let message = target.querySelector(`#${messageId}`);

      if (!hasConflict) {
        message?.remove();
        return;
      }

      if (!message) {
        message = document.createElement('p');
        message.id = messageId;
        message.className = 'oettinger-shipping-guard__message';
        message.setAttribute('role', 'alert');
        target.prepend(message);
      }

      message.textContent = this.getShippingGuardMessage(target);
    });
  }

  getShippingGuardMessage(target) {
    const source = target.closest?.('[data-oettinger-shipping-guard-message]');
    return source?.dataset.oettingerShippingGuardMessage || '';
  }

  getMessageId(element) {
    return element.closest?.('cart-drawer') ? 'OettingerShippingGuardMessageDrawer' : 'OettingerShippingGuardMessagePage';
  }

  areTermsAccepted(button) {
    const formId = button.getAttribute('form');
    const form = formId ? document.getElementById(formId) : button.closest('form');
    const scope = button.closest('cart-drawer, #main-cart-footer') || form || document;
    const termsCheckbox = scope.querySelector?.('#CartDrawer-Terms, [data-oettinger-cart-page-terms-checkbox]');

    return !termsCheckbox || termsCheckbox.checked;
  }

  isTermsCheckbox(element) {
    return element?.matches?.('#CartDrawer-Terms, [data-oettinger-cart-page-terms-checkbox]');
  }
}

if (!window.oettingerShippingGuard) {
  window.oettingerShippingGuard = new OettingerShippingGuard();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => window.oettingerShippingGuard.init(), { once: true });
  } else {
    window.oettingerShippingGuard.init();
  }
}
